import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Tags, Users, MousePointerClick, Eye, Star, TrendingUp, ArrowRight } from "lucide-react";
import { api, fmtPrice } from "../../lib/api";

const StatCard = ({ icon: Icon, label, value, accent = false, testid }) => (
    <div data-testid={testid} className={`p-5 rounded-sm border ${accent ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-brand-border"}`}>
        <div className="flex items-center justify-between">
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${accent ? "text-brand-gold" : "text-brand-gold"}`}>{label}</div>
            <Icon className={`w-4 h-4 ${accent ? "text-brand-gold" : "text-brand-inkSoft"}`} strokeWidth={1.75} />
        </div>
        <div className={`mt-3 font-heading font-bold text-3xl ${accent ? "text-white" : "text-brand-ink"}`}>{value}</div>
    </div>
);

const DashboardHome = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/admin/dashboard").then(({ data }) => setData(data));
    }, []);

    if (!data) return <div className="text-brand-inkSoft" data-testid="dashboard-loading">Carregando…</div>;

    const t = data.totals;

    return (
        <div data-testid="admin-dashboard-home" className="space-y-8">
            <header>
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1">Painel</div>
                <h1 className="font-heading font-bold text-3xl text-brand-ink">Dashboard</h1>
                <p className="text-sm text-brand-inkSoft mt-1">Visão geral da sua loja Vitrine Deus Proverá.</p>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard testid="stat-products" icon={Package} label="Produtos" value={t.products} />
                <StatCard testid="stat-categories" icon={Tags} label="Categorias" value={t.categories} />
                <StatCard testid="stat-leads" icon={Users} label="Leads" value={t.leads} />
                <StatCard testid="stat-featured" icon={Star} label="Em destaque" value={t.featured} />
                <StatCard testid="stat-home-views" icon={Eye} label="Visitas (home)" value={t.home_views} />
                <StatCard testid="stat-product-views" icon={Eye} label="Visualizações" value={t.product_views} />
                <StatCard testid="stat-ml-clicks" icon={MousePointerClick} label="Cliques ML" value={t.ml_clicks} accent />
                <StatCard testid="stat-active" icon={TrendingUp} label="Ativos" value={t.active_products} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-brand-border rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink">Mais clicados (Comprar agora)</h3>
                        <Link to="/admin/metricas" className="text-xs text-brand-gold font-semibold hover:underline">Ver tudo</Link>
                    </div>
                    {data.top_clicked.length === 0 ? (
                        <div className="text-sm text-brand-inkSoft py-8 text-center">Sem cliques registrados ainda. Os números aparecem quando os clientes clicarem no botão "Comprar agora no Mercado Livre".</div>
                    ) : (
                        <ul className="divide-y divide-brand-border">
                            {data.top_clicked.map((p) => (
                                <li key={p.id} className="py-3 flex items-center gap-3">
                                    <div className="w-10 h-10 bg-brand-bg rounded-sm overflow-hidden">
                                        {p.images?.[0] && <img src={p.images[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                                    </div>
                                    <Link to={`/produtos/${p.slug}`} target="_blank" className="flex-1 text-sm font-medium text-brand-ink hover:text-brand-gold">{p.name}</Link>
                                    <div className="text-sm font-bold text-brand-gold">{p.click_count}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="bg-white border border-brand-border rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink">Leads recentes</h3>
                        <Link to="/admin/leads" className="text-xs text-brand-gold font-semibold hover:underline">Ver todos</Link>
                    </div>
                    {data.recent_leads.length === 0 ? (
                        <div className="text-sm text-brand-inkSoft py-8 text-center">Nenhum lead ainda. Os contatos aparecem aqui quando alguém preencher o formulário "Receba novidades".</div>
                    ) : (
                        <ul className="divide-y divide-brand-border">
                            {data.recent_leads.map((l) => (
                                <li key={l.id} className="py-3 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-brand-ink truncate">{l.name}</div>
                                        <div className="text-xs text-brand-inkSoft truncate">{l.whatsapp} {l.email && `· ${l.email}`}</div>
                                    </div>
                                    <div className="text-[10px] text-brand-inkSoft whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("pt-BR")}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="bg-white border border-brand-border rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink">Produtos em destaque</h3>
                    <Link to="/admin/produtos" className="text-xs text-brand-gold font-semibold hover:underline inline-flex items-center gap-1">Gerenciar <ArrowRight className="w-3 h-3" /></Link>
                </div>
                {data.featured_products.length === 0 ? (
                    <div className="text-sm text-brand-inkSoft py-8 text-center">Nenhum produto marcado como destaque.</div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                        {data.featured_products.map((p) => (
                            <Link key={p.id} to={`/produtos/${p.slug}`} target="_blank" className="group block">
                                <div className="aspect-square bg-brand-bg rounded-sm overflow-hidden">
                                    {p.images?.[0] && <img src={p.images[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                                </div>
                                <div className="text-xs font-medium text-brand-ink mt-2 line-clamp-1">{p.name}</div>
                                <div className="text-xs text-brand-gold font-bold">{fmtPrice(p.price)}</div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardHome;
